import { CodeBracketIcon, FlagIcon, PlayIcon } from "@heroicons/react/20/solid";
import {
	MagnifyingGlassIcon,
	Cog8ToothIcon as ManageIconOutline,
	UserGroupIcon as TeamsIconOutline,
} from "@heroicons/react/24/outline";
import {
	Cog8ToothIcon as ManageIconSolid,
	UserGroupIcon as TeamsIconSolid,
} from "@heroicons/react/24/solid";
import { IncidentOutcomeDisplayNames } from "@referee-fyi/share";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { EventData, Skill, TeamData } from "robotevents";
import { Button } from "~components/Button";
import { EventNewIncidentDialog } from "~components/dialogs/new";
import { IconLabel, Input } from "~components/Input";
import { InspectionStatusDot } from "~components/Inspection";
import { MenuButton } from "~components/MenuButton";
import { Spinner } from "~components/Spinner";
import { Tabs } from "~components/Tabs";
import { TeamMenu } from "~components/TeamMenu";
import { VirtualizedList } from "~components/VirtualizedList";
import type { Incident, RichIncident } from "~utils/data/incident";
import { useNewIncidentDialogState } from "~utils/dialogs/new";
import { filterTeams } from "~utils/filterteams";
import { useAddEventVisited } from "~utils/hooks/history";
import {
	useEventIncidents,
	useTeamInspectionStatus,
} from "~utils/hooks/incident";
import { useEventSkills, useEventTeams } from "~utils/hooks/robotevents";
import { useCurrentEvent } from "~utils/hooks/state";
import { EventManageTab } from "./$division/-tabs/manage";

type SkillsTeamListItemProps = {
	team: TeamData;
	sku: string;
	incidents: Incident[];
	driver: Skill | undefined;
	programming: Skill | undefined;
	onNewIncident: (initial: Partial<RichIncident>) => void;
};

const SkillsTeamListItem: React.FC<SkillsTeamListItemProps> = ({
	team,
	sku,
	incidents,
	driver,
	programming,
	onNewIncident,
}) => {
	const inspectionStatus = useTeamInspectionStatus(team.number, sku);

	return (
		<MenuButton
			menu={
				<TeamMenu
					team={team}
					sku={sku}
					incidents={incidents}
					filter={(incident) =>
						incident.team === team.number && incident.match?.type !== "match"
					}
					headerAside={
						<>
							<span className="mr-4">
								<PlayIcon height={20} className="inline" />
								<span className="font-mono ml-2">{driver?.attempts ?? 0}</span>
							</span>
							<span>
								<CodeBracketIcon height={20} className="inline" />
								<span className="font-mono ml-2">
									{programming?.attempts ?? 0}
								</span>
							</span>
						</>
					}
					inspectionChipPlacement="body"
				>
					<Button
						mode="normal"
						align="left"
						className="mt-4 bg-emerald-600"
						onClick={() =>
							onNewIncident({
								team: team.number,
								match: undefined,
								skills: undefined,
								outcome: "InspectionPassed",
							})
						}
					>
						<FlagIcon height={20} className="inline mr-2 " />
						{IncidentOutcomeDisplayNames.InspectionPassed}
					</Button>
					<Button
						mode="normal"
						align="left"
						className="mt-4 bg-red-500"
						onClick={() =>
							onNewIncident({
								team: team.number,
								match: undefined,
								skills: undefined,
								outcome: "InspectionFailed",
							})
						}
					>
						<FlagIcon height={20} className="inline mr-2 " />
						{IncidentOutcomeDisplayNames.InspectionFailed}
					</Button>
					<hr className="mt-4 border-zinc-600" />
					<Button
						mode="normal"
						align="left"
						className="mt-4"
						onClick={() =>
							onNewIncident({
								team: team.number,
								match: undefined,
								skills: undefined,
								outcome: "General",
							})
						}
					>
						<FlagIcon height={20} className="inline mr-2 " />
						{IncidentOutcomeDisplayNames.General}
					</Button>
					<Button
						mode="normal"
						align="left"
						className="mt-4"
						onClick={() =>
							onNewIncident({
								team: team.number,
								skills: {
									type: "skills",
									skillsType: "driver",
									attempt: Math.min(3, (driver?.attempts ?? 0) + 1),
								},
								outcome: "Minor",
							})
						}
					>
						<FlagIcon height={20} className="inline mr-2 " />
						Driver Skills
					</Button>
					<Button
						mode="normal"
						align="left"
						className="mt-4"
						onClick={() =>
							onNewIncident({
								team: team.number,
								skills: {
									type: "skills",
									skillsType: "programming",
									attempt: Math.min(3, (programming?.attempts ?? 0) + 1),
								},
								outcome: "Minor",
							})
						}
					>
						<FlagIcon height={20} className="inline mr-2 " />
						Auto Skills
					</Button>
				</TeamMenu>
			}
			mode="transparent"
			className="flex items-center gap-4 mt-4 h-12 text-zinc-50"
		>
			<div className="flex-1">
				<p className="text-emerald-400 font-mono flex items-center gap-1.5">
					{team.number}
					<InspectionStatusDot status={inspectionStatus.status} />
				</p>
				<p className="overflow-hidden whitespace-nowrap text-ellipsis max-w-[20ch] lg:max-w-prose">
					{team.team_name}
				</p>
			</div>
			<div className="absolute right-0 bg-zinc-800 h-full pl-2 flex items-center">
				<span className="mr-4">
					<PlayIcon height={20} className="inline" />
					<span className="font-mono ml-2">{driver?.attempts ?? 0}</span>
				</span>
				<span className="">
					<CodeBracketIcon height={20} className="inline" />
					<span className="font-mono ml-2">{programming?.attempts ?? 0}</span>
				</span>
			</div>
		</MenuButton>
	);
};

type TeamSkillsTabProps = {
	event: EventData;
};

const TeamSkillsTab: React.FC<TeamSkillsTabProps> = ({ event }) => {
	const { data: incidents } = useEventIncidents(event?.sku);
	const { data: teams, isLoading: isLoadingTeams } = useEventTeams(event);
	const { data: skills, isLoading: isLoadingSkills } = useEventSkills(event);
	const [filter, setFilter] = useState("");

	const newIncidentDialog = useNewIncidentDialogState();
	const openNewIncidentDialog = useCallback(
		(initial: Partial<RichIncident> = {}) => {
			newIncidentDialog.setOpen({
				open: true,
				initial: {
					...initial,
				},
			});
		},
		[newIncidentDialog],
	);

	const skillsByTeam = useMemo(() => {
		if (!skills) {
			return {};
		}

		const teams: Record<string, Skill[]> = {};

		for (const skill of skills) {
			if (!skill.team) continue;
			if (teams[skill.team.name]) {
				teams[skill.team.name].push(skill);
			} else {
				teams[skill.team.name] = [skill];
			}
		}

		return teams;
	}, [skills]);

	const isLoading = isLoadingTeams || isLoadingSkills;

	const filteredTeams = useMemo(
		() => (teams ? filterTeams(teams, filter) : []),
		[filter, teams],
	);

	return (
		<section className="contents mb-12">
			<IconLabel icon={<MagnifyingGlassIcon height={24} />}>
				<Input
					placeholder="Search teams..."
					className="flex-1"
					value={filter}
					onChange={(e) => setFilter(e.currentTarget.value.toUpperCase())}
				/>
			</IconLabel>
			<EventNewIncidentDialog state={newIncidentDialog} />
			<Spinner show={isLoading} />
			<VirtualizedList
				className="flex-1"
				data={filteredTeams}
				options={{ estimateSize: () => 64 }}
				parts={{ list: { className: "mb-12" } }}
			>
				{(team) => {
					if (!team) {
						return null;
					}

					const programming = skillsByTeam[team.number]?.find(
						(skill) => skill.type === "programming",
					);

					const driver = skillsByTeam[team.number]?.find(
						(skill) => skill.type === "driver",
					);

					return (
						<SkillsTeamListItem
							team={team}
							sku={event.sku}
							incidents={incidents ?? []}
							driver={driver}
							programming={programming}
							onNewIncident={openNewIncidentDialog}
						/>
					);
				}}
			</VirtualizedList>
		</section>
	);
};

export const EventSkillsPage: React.FC = () => {
	const { data: event } = useCurrentEvent();

	const { mutateAsync: addEvent, isSuccess } = useAddEventVisited();

	useEffect(() => {
		if (event && !isSuccess) {
			addEvent(event);
		}
	}, [event, isSuccess, addEvent]);

	if (!event) {
		return <Spinner show />;
	}

	return (
		<section className="mt-4 flex flex-col">
			<Tabs
				id={["/$sku/skills", "EventSkillsPage"]}
				className="flex-1"
				parts={{
					tablist: {
						className: "absolute bottom-0 right-0 left-0 z-10 p-0 bg-zinc-900",
					},
				}}
			>
				{[
					{
						type: "content",
						id: "team",
						label: "Teams",
						icon: (active) =>
							active ? (
								<TeamsIconSolid height={24} className="inline" />
							) : (
								<TeamsIconOutline height={24} className="inline" />
							),
						content: <TeamSkillsTab event={event} />,
					},
					{
						type: "content",
						id: "manage",
						label: "Manage",
						icon: (active) =>
							active ? (
								<ManageIconSolid height={24} className="inline" />
							) : (
								<ManageIconOutline height={24} className="inline" />
							),
						content: <EventManageTab event={event} />,
					},
				]}
			</Tabs>
		</section>
	);
};

export const Route = createFileRoute("/$sku/skills")({
	component: EventSkillsPage,
});
